// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Mic recorder: raw mono PCM via an AudioWorklet. getUserMedia is called from a
// tap handler by the UI. We never connect the mic to the destination, so there
// is no monitoring feedback and nothing routes to the iOS earpiece. Every track
// is stopped and the context closed on stop/teardown so the OS mic dot goes out.

export interface RecordingResult {
  samples: Float32Array;
  sampleRate: number;
}

export class MicRecorder {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private chunks: Float32Array[] = [];
  private startedAt = 0;

  /** Whether the browser can record at all. */
  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof AudioWorkletNode !== 'undefined'
    );
  }

  /**
   * Begin recording. MUST be called from a user gesture. `onLevel` receives a
   * 0..1 peak for a live meter; `onTick` receives elapsed seconds.
   */
  async start(onLevel: (peak: number) => void): Promise<void> {
    this.chunks = [];
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
      video: false,
    });
    this.stream = stream;

    const Ctor: typeof AudioContext =
      (window.AudioContext as typeof AudioContext) ||
      ((window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    const ctx = new Ctor();
    this.ctx = ctx;
    if (ctx.state === 'suspended') await ctx.resume();

    const base = (import.meta as unknown as { env: { BASE_URL: string } }).env.BASE_URL || '/';
    await ctx.audioWorklet.addModule(`${base}capture-worklet.js`);

    const source = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, 'hushwell-capture');
    node.port.onmessage = (e: MessageEvent) => {
      const d = e.data;
      if (d instanceof Float32Array) {
        this.chunks.push(d);
      } else if (d && typeof d.peak === 'number') {
        onLevel(d.peak);
      }
    };
    source.connect(node);
    // Deliberately NOT connected to ctx.destination — no monitoring/feedback.
    this.source = source;
    this.node = node;
    this.startedAt = ctx.currentTime;
  }

  /** Elapsed recording seconds (0 if not recording). */
  elapsed(): number {
    if (!this.ctx) return 0;
    return Math.max(0, this.ctx.currentTime - this.startedAt);
  }

  /** Stop recording and return the concatenated PCM + sample rate. */
  async stop(): Promise<RecordingResult> {
    const sampleRate = this.ctx?.sampleRate ?? 48000;
    // Ask the worklet to flush its tail, then wait a beat for the message.
    this.node?.port.postMessage('stop');
    await new Promise((r) => setTimeout(r, 60));
    this.teardown();

    let total = 0;
    for (const c of this.chunks) total += c.length;
    const samples = new Float32Array(total);
    let offset = 0;
    for (const c of this.chunks) {
      samples.set(c, offset);
      offset += c.length;
    }
    this.chunks = [];
    return { samples, sampleRate };
  }

  /** Release the mic and audio graph without returning audio (cancel). */
  teardown(): void {
    try {
      this.source?.disconnect();
      this.node?.disconnect();
    } catch {
      /* ignore */
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.stream = null;
    this.node = null;
    this.source = null;
    this.ctx = null;
  }
}
